import type { FC, ReactElement } from "react";
import { useEffect, useMemo, useRef, useState } from "react";

import { Dropdown, DropdownButton } from "@similarweb/ui-components";

import useActivationQueryWithAccessControl from "app/custom-hooks/activation/useActivationQueryWithAccessControl";
import type { ShortActivationSetSchema } from "app/pages/activation/types/api";
import { ITEMS_RENDER_BUFFER, ITEMS_RENDER_LIMIT } from "pro-features/components/DomainsFilter/Consts";
import type { IChipItemWithOptionType } from "pro-features/components/DomainsFilter/Types";
import {
    createDomainsStringIds,
    createUniqueListOfAllDomains,
    onItemClick,
    renderChipTitleText,
    renderDropDownItem,
} from "pro-features/components/DomainsFilter/Utils";
import { FooterButton } from "pro-features/components/MultiCategoriesChipDown/src/MultiCategoryChipdownStyles";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { translate } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";
import { FlexColumn } from "pro-features/styled components/StyledFlex/src/StyledFlex";

interface IDomainsDashboardFilter {
    domainsList: IChipItemWithOptionType[];
    onSelect: (domain: any) => void;
    domains: string;
    keyword: string;
}

export const DomainsDashboardFilter: FC<IDomainsDashboardFilter> = ({ domainsList, onSelect, domains, keyword }) => {
    const [selectedItems, setSelectedItems] = useState<IChipItemWithOptionType[]>([]);
    const [wereChangesMade, setWereChangesMade] = useState<boolean>(false);
    const dropDownRef = useRef(null);
    const {
        data: activationSetsData,
        isLoading: loadingActivationSet,
        isFetching: fetchingActivationSet,
    } = useActivationQueryWithAccessControl();
    const [activationSets, setActivationSets] = useState<ShortActivationSetSchema[]>(undefined);

    const handleDomainsFilterOpened = (isOpened: boolean) => {
        if (isOpened) {
            allTrackers.dispatchTrackEvent("Drop Down", "open", "Category/filter opened");
        }
    };

    useEffect(() => {
        if (!loadingActivationSet && !fetchingActivationSet) {
            setActivationSets(activationSetsData);
        }
    }, [loadingActivationSet, fetchingActivationSet]);

    const resetState = () => {
        if (domains && domainsList.length > 0) {
            const domainItems = domains.split(",");
            const cleanedDomains = domainItems.map((domain) => (domain.startsWith("*") ? domain.substring(1) : domain));
            const items = domainsList.filter((item) => cleanedDomains.includes(item.id));
            setSelectedItems(items);
        }
    };

    useEffect(() => {
        if (!domainsList) {
            return;
        }
        resetState();
    }, [domainsList]);

    const onDone = () => {
        dropDownRef.current.closePopup();
        if (!wereChangesMade) {
            return;
        }
        const domainsStringIds = createDomainsStringIds(selectedItems, domainsList, activationSets);
        allTrackers.dispatchTrackEvent(
            "Drop Down",
            "submit-ok",
            `${
                createUniqueListOfAllDomains(domainsStringIds?.split(","), activationSets) ??
                "No Domains/Project Selected"
            }`,
        );

        onSelect(domainsStringIds);
    };

    const selectedItemsIds = Object.fromEntries(selectedItems.map((item) => [item.id, true]));

    const onChipClicked = (item: IChipItemWithOptionType) => {
        const newSelectedItems = onItemClick(item, selectedItems, domainsList);
        setSelectedItems(newSelectedItems);
        setWereChangesMade(true);
    };

    const onItemClicked = (item: IChipItemWithOptionType) => {
        const domain = domainsList.find((domain) => domain.id === item.id);
        if (domain) {
            onChipClicked(domain);
        }
    };

    const searchFilter = (
        term: string,
        item: ReactElement<{
            iconName?: string;
            children: ReactElement<{ children: ReactElement<{ id: string }>[] }>;
            id: string;
        }>,
    ): boolean => {
        return (item.props.iconName ? item.props.children.props.children[0].props.id : item.props.id)
            .toLowerCase()
            .startsWith(term.toLowerCase());
    };

    const renderDomainsItem = useMemo(() => {
        return domainsList?.map((item) => renderDropDownItem(selectedItems, domainsList, activationSets)(item)) ?? [];
    }, [domainsList, selectedItems, activationSets]);
    return (
        <FlexColumn>
            <Dropdown
                ref={dropDownRef}
                closeOnItemClick={false}
                onToggle={handleDomainsFilterOpened}
                selectedIds={selectedItemsIds}
                hasSearch={true}
                searchPlaceHolder={translate("keywords.analysis.filters.domains.projects.searchPlaceHolder")}
                footerComponent={() =>
                    wereChangesMade && <FooterButton onClick={onDone}>{<I18n>common.apply</I18n>}</FooterButton>
                }
                disabled={false}
                onClick={onItemClicked}
                virtualize={domainsList?.length > ITEMS_RENDER_LIMIT ? { itemBuffer: ITEMS_RENDER_BUFFER } : null}
                maxVirtualItemsToRender={domainsList?.length > ITEMS_RENDER_LIMIT ? ITEMS_RENDER_LIMIT : null}
                searchFilter={searchFilter}
            >
                {[
                    <DropdownButton
                        key="DropdownButton1"
                        cssClass="DropdownButton--has-value"
                    >
                        {selectedItems.length > 0
                            ? renderChipTitleText(selectedItems, activationSets)
                            : translate("keywords.analysis.filters.domains.projects.button")}
                    </DropdownButton>,
                    ...renderDomainsItem,
                ]}
            </Dropdown>
        </FlexColumn>
    );
};
